import React from 'react';
import { useEmployees } from '@/api/hooks/useEmployees';
import { useInvites } from '@/api/hooks/useInvites';
import { Employees } from '@/types/Employee/Employee';
import { Invitation } from '@/types/Invitation/Invitation';
import { createContext, ReactNode, useContext, useState } from 'react';

interface EmployeesPageContextApi {
  employees: Employees;
  employeesIsLoading: boolean;
  refetchEmployees: () => void;
  invites: Invitation[];
  invitesIsLoading: boolean;
  refetchInvites: () => void;
  openInviteDialog: boolean;
  setOpenInviteDialog: (value: boolean) => void;
  openDeleteInvitationDialog: boolean;
  setOpenDeleteInvitationDialog: (value: boolean) => void;
}

const EmployeesPageContext = createContext<EmployeesPageContextApi | undefined>(undefined);

interface EmployeesPageProviderProps {
  children: ReactNode;
}

function EmployeesPageProvider({ children }: EmployeesPageProviderProps) {
  const { employees, isLoading: employeesIsLoading, refetch: refetchEmployees } = useEmployees();
  const { invites, invitesIsLoading, refetch: refetchInvites } = useInvites();
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [openDeleteInvitationDialog, setOpenDeleteInvitationDialog] = useState(false);

  const value: EmployeesPageContextApi = {
    employees,
    employeesIsLoading,
    refetchEmployees,
    invites,
    invitesIsLoading,
    refetchInvites,
    openInviteDialog,
    setOpenInviteDialog,
    openDeleteInvitationDialog,
    setOpenDeleteInvitationDialog,
  };

  return <EmployeesPageContext.Provider value={value}>{children}</EmployeesPageContext.Provider>;
}

function useEmployeesPageContext() {
  const context = useContext(EmployeesPageContext);

  if (context === undefined) {
    throw new Error('useEmployeesPageContext must be used within an EmployeesPageProvider');
  }

  return context;
}

export { EmployeesPageProvider, useEmployeesPageContext };
